import { useState } from 'react';
import { isUncertainFailure, useOperationKey } from './walletShared';
import { useTranslation } from 'react-i18next';

export interface LedgerOperation {
  value: string;
  label: string;
}

/** Operation radio + amount + note + save, shared by every points / ZIPA tab. */
export function LedgerAdjustForm({
  operations,
  amountLabel,
  step,
  pending,
  error,
  onSubmit,
  keyed,
}: {
  operations: LedgerOperation[];
  amountLabel: string;
  step: string;
  pending: boolean;
  error: string;
  onSubmit: (input: {
    operation: string;
    amount: string;
    note: string;
    operationKey?: string;
  }) => void | Promise<unknown>;
  keyed?: { scope: string; reload: () => Promise<unknown> };
}) {
  const { t } = useTranslation('member');
  const [operation, setOperation] = useState(operations[0]?.value ?? '');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [retry, setRetry] = useState(false);
  const key = useOperationKey(
    { scope: keyed?.scope, operation, amount, note },
    keyed?.reload ?? (() => Promise.resolve()),
  );
  const valid = Number(amount) > 0 && note.trim().length > 0;
  return (
    <form
      className="bg-base-200 border border-base-300 rounded-lg p-4 space-y-3"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!valid || pending || (keyed && !key.ready)) return;
        try {
          await onSubmit({
            operation,
            amount: amount.trim(),
            note: note.trim(),
            ...(keyed ? { operationKey: key.key } : {}),
          });
          if (keyed) key.renew();
          setRetry(false);
          setAmount('');
          setNote('');
        } catch (failure) {
          if (keyed) {
            key.failed(failure);
            setRetry(isUncertainFailure(failure));
          }
        }
      }}
    >
      <fieldset disabled={pending} className="flex flex-wrap items-center gap-4">
        <legend className="sr-only">{t('ledgerOperation')}</legend>
        <span className="text-sm text-base-content/70">{t('ledgerOperation')}</span>
        {operations.map((op) => (
          <label key={op.value} className="flex items-center gap-2 min-h-11 text-sm">
            <input
              type="radio"
              name="ledger-operation"
              value={op.value}
              checked={operation === op.value}
              onChange={() => setOperation(op.value)}
            />
            {op.label}
          </label>
        ))}
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-base-content/70 mb-1 block">{amountLabel}</span>
          <input
            disabled={pending}
            type="number"
            min="0"
            step={step}
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="w-full px-3 py-2 rounded-md border border-base-300 bg-base-100 min-h-11"
          />
        </label>
        <label className="block text-sm">
          <span className="text-base-content/70 mb-1 block">{t('ledgerNote')}</span>
          <input
            disabled={pending}
            type="text"
            maxLength={255}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="w-full px-3 py-2 rounded-md border border-base-300 bg-base-100 min-h-11"
          />
        </label>
      </div>
      {error && (
        <p role="alert" className="text-sm text-error">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={!valid || pending || (!!keyed && !key.ready)}
        className="min-h-11 px-4 py-2 rounded-md bg-primary text-primary-content text-sm disabled:opacity-50"
      >
        {pending ? t('ledgerSaving') : t(retry ? 'ledgerRetry' : 'ledgerSave')}
      </button>
    </form>
  );
}
